import { createReducer, on, Action, ActionReducer } from '@ngrx/store';
import { PaginationState, SortState } from './models';
import { PaginationChangedProps, SortChangedProps, LoadWithPaginationActions } from './actions';
import {
  filterChangedReducer,
  loadErroredReducer,
  loadReducer,
  loadSucceededReducer,
  LoadState,
  resetError,
  setLoading,
} from '../load/reducer';
import { mergeReducers } from '../utils';

export interface LoadWithPaginationState<Entity> extends LoadState<Entity> {
  pagination: PaginationState;
  sort: SortState;
}

const defaultPagination = <Entity>(state: LoadWithPaginationState<Entity>): PaginationState => ({
  limit: state.pagination.limit,
  page: 1,
  total: 0,
});

export const resetPagination = <Entity>(
  state: LoadWithPaginationState<Entity>,
): LoadWithPaginationState<Entity> => ({ ...state, pagination: defaultPagination(state) });

export const changePagination = <Entity>(
  state: LoadWithPaginationState<Entity>,
  { pagination }: PaginationChangedProps,
): LoadWithPaginationState<Entity> => ({
  ...state,
  pagination: { ...state.pagination, ...pagination },
});

export const sortChanged = <Entity>(
  state: LoadWithPaginationState<Entity>,
  { sort }: SortChangedProps,
): LoadWithPaginationState<Entity> => ({ ...state, sort });

export function createLoadWithPaginationReducer<Entity>(
  initialState: LoadWithPaginationState<Entity>,
  actions: LoadWithPaginationActions<Entity>,
): ActionReducer<LoadWithPaginationState<Entity>, Action> {
  return createReducer(
    initialState,
    on(actions.load, mergeReducers(loadReducer, resetPagination)),
    on(actions.filterChanged, mergeReducers(filterChangedReducer, resetPagination)),
    on(actions.paginationChanged, mergeReducers(changePagination, setLoading, resetError)),
    on(actions.sortChanged, mergeReducers(sortChanged, setLoading, resetError)),
    on(actions.loadSucceeded, mergeReducers(loadSucceededReducer, changePagination)),
    on(actions.loadErrored, loadErroredReducer),
  );
}
